import React, { useContext } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import Icon from '@ant-design/icons';
import dayjs from 'dayjs';
import classnames from 'classnames';

import { sendSegment } from 'utils/segment';
import { SLLR_KEY } from 'constants/sllr';
import { SLLR_ICON } from 'utils/helpers';
import {
  FULFILLMENT_NOTIFICATION,
  VIEWED_STATUS,
  TICKET_NOTIFICATION,
  DRAFT_ORDER_NOTIFICATION,
  BUSINESS_TIER_DOWNGRADED_NOTIFICATION,
  STAR_RESCHEDULED_PICKUP_NOTIFICATION,
  CANCELED_RECURRING_PICKUPS_NOTIFICATION,
  CANCELED_PICKUPS_WITH_NO_NEW_ORDERS_NOTIFICATION,
  PICKUP_NOTIFICATION,
  DELETED_ORDER_RESTORED,
  BUSINESS_PRICE_PLAN_UPGRADED_NOTIFICATION,
  BUSINESS_PRICE_PLAN_DOWNGRADED_NOTIFICATION_TYPE,
  BUSINESS_PRICE_NOTIFY_DOWNGRADING_NOTIFICATION_TYPE,
  BUSINESS_PRICE_ENCOURAGEMENT_NOTIFICATION_TYPE,
  SUCCESSFUL_PICKUP_NOTIFICATION
} from 'constants/notifications';
import { INSTAGRAM, MESSENGER, WHATSAPP } from 'constants/chats';
import { ContextWrapper } from 'contexts/wrapper.context';
import { useModal } from 'contexts/modalProvider.context';
import RatePickupServiceModal from 'components/Pickups/components/RatePickupServiceModal/RatePickupServiceModal';

import { ReactComponent as TicketNotification } from 'assets/bosta-icons/ticket-notification.svg';
import { ReactComponent as PickupNotification } from 'assets/bosta-icons/pickup-notification.svg';
import { ReactComponent as InventoryNotification } from 'assets/bosta-icons/inventory-notification.svg';
import { ReactComponent as DraftOrderNotification } from 'assets/bosta-icons/draft-order-notification.svg';
import { ReactComponent as MessengerIcon } from 'assets/bosta-icons/connectMessanger.svg';
import { ReactComponent as WhatsappIcon } from 'assets/bosta-icons/whatsapp.svg';
import { ReactComponent as InstagramIcon } from 'assets/bosta-icons/instagram.svg';
import { ReactComponent as SllrCutLogoXsmall } from 'assets/icons/sllr-green-icon-xsmall.svg';
import { ReactComponent as UpgradeNotificationIcon } from 'assets/bosta-icons/sllr-draft-order.svg';

import './SingleNotification.less';

const SingleNotification = ({ item, history, intl }) => {
  const { handleOpenCreateEditPickupModal } = useContext(ContextWrapper);
  const { openModal } = useModal();

  const handleOnClick = (item) => {
    sendSegment('E_NOTIFICATION_CLICKED', []);
    const { extra } = item;

    if (item.type === TICKET_NOTIFICATION) {
      history.push({
        pathname: '/support-tickets',
        state: {
          externalId: extra?.ticketId
        }
      });
    } else if (item.type === FULFILLMENT_NOTIFICATION) {
      history.push({
        pathname: '/inventory',
        search: `bostaSku=${extra?.bostaSku}`
      });
    } else if (item.type === DRAFT_ORDER_NOTIFICATION) {
      history.push(`/edit-order/${extra?.salesOrderId}?draft=true`);
    } else if (
      [
        BUSINESS_TIER_DOWNGRADED_NOTIFICATION,
        CANCELED_PICKUPS_WITH_NO_NEW_ORDERS_NOTIFICATION
      ].includes(item.type)
    ) {
      return;
    } else if (
      [
        STAR_RESCHEDULED_PICKUP_NOTIFICATION,
        CANCELED_RECURRING_PICKUPS_NOTIFICATION
      ].includes(item.type)
    ) {
      history.push(`/pickups/${extra?.puid}`);
    } else if (item.type === PICKUP_NOTIFICATION && !extra?.pickupId) {
      {
        handleOpenCreateEditPickupModal({ openModal });
      }
    } else if (item.type === DELETED_ORDER_RESTORED) {
      history.push(`/orders/${extra?.trackingNumber}`);
    } else if (
      [
        BUSINESS_PRICE_PLAN_UPGRADED_NOTIFICATION,
        BUSINESS_PRICE_ENCOURAGEMENT_NOTIFICATION_TYPE,
        BUSINESS_PRICE_NOTIFY_DOWNGRADING_NOTIFICATION_TYPE,
        BUSINESS_PRICE_PLAN_DOWNGRADED_NOTIFICATION_TYPE
      ].includes(item.type)
    ) {
      history.push({
        pathname: '/orders'
      });
    } else if (item.type === SUCCESSFUL_PICKUP_NOTIFICATION) {
      openModal(RatePickupServiceModal, {
        pickupId: extra?.puid,
        pickedupDate: extra?.pickedupDate
      });
    } else {
      history.push(`/pickups/${extra?.pickupId}`);
    }
  };

  const renderTimeFormate = () => {
    if (dayjs(item.createdAt).minute() === dayjs(new Date()).minute()) {
      return intl.formatMessage({
        id: 'notification.today_now'
      });
    } else if (
      dayjs(item.createdAt).date() === dayjs(new Date()).date() &&
      dayjs(item.createdAt).month() === dayjs(new Date()).month()
    ) {
      return `${dayjs(item.createdAt).fromNow(true)} ${intl.formatMessage({
        id: 'notification.ago'
      })}`;
    } else if (dayjs(item.createdAt).date() + 1 === dayjs(new Date()).date()) {
      return `${intl.formatMessage({
        id: 'notification.yesterday'
      })} - ${dayjs(item.createdAt).format('hh:mm A')}`;
    } else {
      return dayjs(item.createdAt).format('DD MMM YYYY - hh:mm A');
    }
  };

  const renderNotificationSrcIcon = (src) => {
    switch (src) {
      case MESSENGER:
        return MessengerIcon;
      case WHATSAPP:
        return WhatsappIcon;
      case INSTAGRAM:
        return InstagramIcon;
      default:
        return SllrCutLogoXsmall;
    }
  };

  const getNotificationIcon = (type, src = SLLR_KEY) => {
    switch (type) {
      case TICKET_NOTIFICATION:
        return TicketNotification;
      case FULFILLMENT_NOTIFICATION:
        return InventoryNotification;
      case DRAFT_ORDER_NOTIFICATION:
        return renderNotificationSrcIcon(src);
      case BUSINESS_PRICE_PLAN_UPGRADED_NOTIFICATION:
        return UpgradeNotificationIcon;
      case BUSINESS_PRICE_PLAN_DOWNGRADED_NOTIFICATION_TYPE:
        return UpgradeNotificationIcon;
      case BUSINESS_PRICE_ENCOURAGEMENT_NOTIFICATION_TYPE:
        return UpgradeNotificationIcon;
      case BUSINESS_PRICE_NOTIFY_DOWNGRADING_NOTIFICATION_TYPE:
        return UpgradeNotificationIcon;
      default:
        return PickupNotification;
    }
  };

  const getNotificationIconStyle = (type) => {
    switch (type) {
      case FULFILLMENT_NOTIFICATION:
        return 'br-single-notification__icon-inventory';
      case DRAFT_ORDER_NOTIFICATION:
        return 'br-single-notification__icon-draft-order';
      case BUSINESS_PRICE_PLAN_UPGRADED_NOTIFICATION:
        return 'br-single-notification__icon-upgrade';
      case BUSINESS_PRICE_PLAN_DOWNGRADED_NOTIFICATION_TYPE:
        return 'br-single-notification__icon-upgrade';
      case BUSINESS_PRICE_ENCOURAGEMENT_NOTIFICATION_TYPE:
        return 'br-single-notification__icon-upgrade';
      case BUSINESS_PRICE_NOTIFY_DOWNGRADING_NOTIFICATION_TYPE:
        return 'br-single-notification__icon-upgrade';
      default:
        return '';
    }
  };

  const getNotificationLabelStyle = (type) => {
    switch (type) {
      case DRAFT_ORDER_NOTIFICATION:
        return 'br-single-notification__label-draft-order';
      default:
        return 'br-single-notification__label-default';
    }
  };

  const getNotificationTitle = (item) => {
    const { type, title, extra } = item;
    switch (type) {
      case DRAFT_ORDER_NOTIFICATION:
        return intl.formatMessage({
          id: 'notification.draft_orders_notifications.title'
        });
      case BUSINESS_TIER_DOWNGRADED_NOTIFICATION:
        return intl.formatMessage({
          id: 'notification.business_tier_downgraded_notification.title'
        });
      case STAR_RESCHEDULED_PICKUP_NOTIFICATION:
        return intl.formatMessage({
          id: 'notification.star_rescheduled_pickup_notification.title'
        });
      case CANCELED_RECURRING_PICKUPS_NOTIFICATION:
        return intl.formatMessage({
          id: 'notification.canceled_recurring_pickup_notification.title'
        });
      case CANCELED_PICKUPS_WITH_NO_NEW_ORDERS_NOTIFICATION:
        return intl.formatMessage({
          id: 'notification.cancelled_pickup_with_no_new_orders_notification.title'
        });
      case DELETED_ORDER_RESTORED:
        return intl.formatMessage({
          id: 'notification.deleted_order_restored.title'
        });
      case BUSINESS_PRICE_PLAN_UPGRADED_NOTIFICATION:
        return intl.formatMessage({
          id: 'notification.business_price_plan_upgraded.title'
        });
      case BUSINESS_PRICE_PLAN_DOWNGRADED_NOTIFICATION_TYPE:
        return intl.formatMessage({
          id: 'notification.business_price_plan_downgraded.title'
        });

      case BUSINESS_PRICE_ENCOURAGEMENT_NOTIFICATION_TYPE:
        return intl.formatMessage(
          {
            id: 'notification.business_price_plan_encourage_notify.title'
          },
          {
            target: extra.tierOrderLimit
          }
        );
      case BUSINESS_PRICE_NOTIFY_DOWNGRADING_NOTIFICATION_TYPE:
        return intl.formatMessage({
          id: 'notification.business_price_plan_downgrading_notify.title'
        });
      case SUCCESSFUL_PICKUP_NOTIFICATION:
        return extra?.puid;
      default:
        return title;
    }
  };

  const orderSrcMapper = (src = SLLR_KEY) => {
    switch (src) {
      case MESSENGER:
        return 'products.sllr_store.messenger';
      case WHATSAPP:
        return 'products.sllr_store.whatsapp';
      case INSTAGRAM:
        return 'products.sllr_store.instagram';
      default:
        return 'products.sllr_store.sllr_store';
    }
  };

  const getNotificationContent = (item) => {
    const { type, extra } = item;

    switch (type) {
      case DRAFT_ORDER_NOTIFICATION:
        return intl.formatMessage(
          {
            id: `notification.draft_orders_notifications.content_${
              extra.itemCount === 1 ? 'single' : 'multiple'
            }_item`
          },
          {
            itemCount: <span>{extra.itemCount}</span>,
            consigneeName: <span>{extra.consigneeName}</span>,
            orderSrc: intl.formatMessage({
              id: orderSrcMapper(extra?.salesChannel)
            })
          }
        );
      case BUSINESS_PRICE_PLAN_DOWNGRADED_NOTIFICATION_TYPE:
        return intl.formatMessage(
          {
            id: 'notification.business_price_plan_downgraded.content'
          },
          {
            missedTarget: extra.tierOrderLimit
          }
        );
      case STAR_RESCHEDULED_PICKUP_NOTIFICATION:
        return intl.formatMessage(
          {
            id: 'notification.star_rescheduled_pickup_notification.content'
          },
          {
            puid: extra?.puid,
            locationName: extra?.locationName,
            scheduledDate: dayjs(extra?.oldScheduledDate).format('D MMM')
          }
        );
      case CANCELED_RECURRING_PICKUPS_NOTIFICATION:
        return intl.formatMessage(
          {
            id: 'notification.canceled_recurring_pickup_notification.content'
          },
          {
            puid: extra?.puid,
            locationName: extra?.locationName,
            canceledDay: intl.formatMessage({
              id: `pickups.repeated_modal.days.${extra?.canceledDay?.toLowerCase()}`
            })
          }
        );
      case CANCELED_PICKUPS_WITH_NO_NEW_ORDERS_NOTIFICATION:
        return intl.formatMessage({
          id: 'notification.cancelled_pickup_with_no_new_orders_notification.content'
        });
      case DELETED_ORDER_RESTORED:
        return intl.formatMessage({
          id: 'notification.deleted_order_restored.content'
        });
      case BUSINESS_PRICE_PLAN_UPGRADED_NOTIFICATION:
        return intl.formatMessage(
          {
            id: 'notification.business_price_plan_upgraded.content'
          },
          {
            target: item?.extra?.achievedTarget
          }
        );
      case BUSINESS_PRICE_PLAN_DOWNGRADED_NOTIFICATION_TYPE:
        return intl.formatMessage(
          {
            id: 'notification.business_price_plan_downgraded.content'
          },
          {
            missedTarget: extra.tierOrderLimit
          }
        );

      case BUSINESS_PRICE_ENCOURAGEMENT_NOTIFICATION_TYPE:
        return intl.formatMessage(
          {
            id: 'notification.business_price_plan_encourage_notify.content'
          },
          {
            target: extra.tierOrderLimit
          }
        );
      case BUSINESS_PRICE_NOTIFY_DOWNGRADING_NOTIFICATION_TYPE:
        return intl.formatMessage(
          {
            id: 'notification.business_price_plan_downgrading_notify.content'
          },
          {
            missedTarget: extra.tierOrderLimit
          }
        );
      case SUCCESSFUL_PICKUP_NOTIFICATION:
        return intl.formatMessage({
          id: 'notification.successful_pickup_notification.content'
        });
      default:
        return item.text.split('\n').map((string) => <div>{string}</div>);
    }
  };

  return (
    <div
      className="br-single-notification__container"
      onClick={() => handleOnClick(item)}
    >
      <div
        className={classnames(
          'br-single-notification__icon',
          `${getNotificationIconStyle(item.type)}`,
          {
            'br-single-notification__icon-unread': item.status !== VIEWED_STATUS
          }
        )}
      >
        <Icon
          component={getNotificationIcon(item.type, item.extra?.salesChannel)}
        />
      </div>
      <div className="br-single-notification__content">
        <div className="br-single-notification__header-container">
          <div className="br-single-notification__header">
            <div className="br-single-notification__title body-medium">
              {getNotificationTitle(item)}
            </div>
            <div className="br-single-notification__time caption">
              {renderTimeFormate()}
            </div>
          </div>
          {item.status !== VIEWED_STATUS && (
            <div className="br-single-notification__unread-dot" />
          )}
        </div>
        <div className="br-single-notification__text body">
          {getNotificationContent(item)}
        </div>
        <div
          className={classnames(
            'br-single-notification__label body',
            `${getNotificationLabelStyle(item.type)}`
          )}
        >
          {intl.formatMessage({
            id: `notification.${item.type}`
          })}
        </div>
      </div>
    </div>
  );
};
export default injectIntl(withRouter(SingleNotification));
